import React, { useState, useEffect } from 'react';
import FocusLock from 'react-focus-lock';
import { Cookie, Settings } from 'lucide-react';
import { initializeGtagDynamically } from '../services/analytics';

/**
 * CookieConsentBanner - GDPR/CCPA compliant cookie consent banner (Issue #1310)
 *
 * Features:
 * - Shows on first visit before analytics loads
 * - Granular consent (Essential, Analytics, Marketing)
 * - "Accept All" / "Reject Non-Essential" / "Customize" buttons
 * - Stores consent in localStorage
 * - Respects Do Not Track (DNT) browser setting
 * - Triggers analytics initialization after consent
 *
 * Consent Keys in localStorage:
 * - cookie_consent_given: 'true' if user made a choice
 * - cookie_consent_essential: 'true' (always true, cannot be disabled)
 * - cookie_consent_analytics: 'true' | 'false'
 * - cookie_consent_marketing: 'true' | 'false'
 */

interface CookieConsentBannerProps {
  onConsentChange?: () => void;
}

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

export default function CookieConsentBanner({ onConsentChange }: CookieConsentBannerProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already given consent
    try {
      const consentGiven = localStorage.getItem('cookie_consent_given') === 'true';
      if (!consentGiven) {
        setIsVisible(true);
      }
    } catch (error) {
      // If localStorage is unavailable, don't show banner
      console.error('[CookieConsent] Failed to check consent:', error);
      setIsVisible(false);
    }
  }, []);

  // Handle Escape key to show customize view
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isVisible && !showCustomize && !isInitializing) {
        setShowCustomize(true);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isVisible, showCustomize, isInitializing]);

  const saveConsent = async (essential: boolean, analytics: boolean, marketing: boolean) => {
    try {
      setIsInitializing(true);

      localStorage.setItem('cookie_consent_given', 'true');
      localStorage.setItem('cookie_consent_essential', 'true'); // Always true
      localStorage.setItem('cookie_consent_analytics', analytics ? 'true' : 'false');
      localStorage.setItem('cookie_consent_marketing', marketing ? 'true' : 'false');

      // Trigger analytics initialization if consent given
      if (analytics) {
        // Remove opt-out flag if it exists
        localStorage.removeItem('analytics_opt_out');

        // Dynamically initialize analytics if consent given (shared function from analytics service)
        initializeGtagDynamically();

        // Give gtag time to load before hiding banner
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        // Set opt-out flag
        localStorage.setItem('analytics_opt_out', 'true');
      }

      setIsVisible(false);

      // Notify parent component of consent change
      if (onConsentChange) {
        onConsentChange();
      }
    } catch (error) {
      console.error('Failed to save cookie consent:', error);
    } finally {
      setIsInitializing(false);
    }
  };


  const handleAcceptAll = () => {
    saveConsent(true, true, true);
  };

  const handleRejectNonEssential = () => {
    saveConsent(true, false, false);
  };

  const handleSaveCustom = () => {
    saveConsent(preferences.essential, preferences.analytics, preferences.marketing);
  };

  const handleCustomize = () => {
    setShowCustomize(true);
  };

  const handleBack = () => {
    setShowCustomize(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <FocusLock disabled={isInitializing}>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center z-[60]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-consent-title"
      >
        <div className="bg-white rounded-t-lg md:rounded-lg shadow-2xl max-w-2xl w-full mx-4 mb-0 md:mb-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Cookie className="w-6 h-6 text-secondary" aria-hidden="true" />
            <h2 id="cookie-consent-title" className="text-xl font-bold text-gray-900">
              {showCustomize ? 'Customize Cookie Preferences' : 'We Value Your Privacy'}
            </h2>
          </div>
        </div>

        {/* Main Content */}
        {!showCustomize ? (
          <>
            {/* Simple View */}
            <div className="p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                We use cookies to enhance your experience, analyze site usage, and assist in our marketing efforts.
                By clicking "Accept All," you consent to our use of cookies.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                You can customize your preferences or reject non-essential cookies. Essential cookies are always enabled
                to ensure the platform functions properly.
              </p>
              <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-primary-dark underline">
                  Privacy Policy
                </a>
                <span>|</span>
                <a href="/cookies" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-primary-dark underline">
                  Cookie Policy
                </a>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 p-6 bg-gray-50 border-t border-gray-200">
              <button
                onClick={handleAcceptAll}
                disabled={isInitializing}
                className="flex-1 bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isInitializing ? 'Saving...' : 'Accept All'}
              </button>
              <button
                onClick={handleRejectNonEssential}
                disabled={isInitializing}
                className="flex-1 bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isInitializing ? 'Saving...' : 'Reject Non-Essential'}
              </button>
              <button
                onClick={handleCustomize}
                disabled={isInitializing}
                className="flex items-center justify-center gap-2 flex-1 bg-white border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Settings className="w-5 h-5" aria-hidden="true" />
                Customize
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Customize View */}
            <div className="p-6">
              <p className="text-gray-700 leading-relaxed mb-6">
                Choose which cookies you want to accept. Essential cookies cannot be disabled as they are required
                for the platform to function.
              </p>

              {/* Essential Cookies */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Essential Cookies
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">
                      Required for the platform to function. These cookies cannot be disabled.
                    </p>
                    <p className="text-xs text-gray-500">
                      Examples: CSRF protection, session management, authentication
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                      Always On
                    </div>
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="mb-6 p-4 bg-white rounded-lg border-2 border-gray-200">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Analytics Cookies
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">
                      Help us understand how you use the platform so we can improve it.
                    </p>
                    <p className="text-xs text-gray-500">
                      Examples: Google Analytics (page views, user behavior, anonymized data)
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => setPreferences({ ...preferences, analytics: !preferences.analytics })}
                      className={`
                        relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                        ${preferences.analytics ? 'bg-primary' : 'bg-gray-300'}
                      `}
                      aria-label="Toggle analytics cookies"
                      aria-checked={preferences.analytics}
                      role="switch"
                    >
                      <span
                        className={`
                          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                          ${preferences.analytics ? 'translate-x-6' : 'translate-x-1'}
                        `}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="mb-6 p-4 bg-white rounded-lg border-2 border-gray-200">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Marketing Cookies
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">
                      Used to deliver personalized ads and offers based on your interests.
                    </p>
                    <p className="text-xs text-gray-500">
                      Examples: Ad tracking, remarketing pixels (currently not used)
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}
                      className={`
                        relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                        ${preferences.marketing ? 'bg-primary' : 'bg-gray-300'}
                      `}
                      aria-label="Toggle marketing cookies"
                      aria-checked={preferences.marketing}
                      role="switch"
                    >
                      <span
                        className={`
                          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                          ${preferences.marketing ? 'translate-x-6' : 'translate-x-1'}
                        `}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* DNT Notice */}
              <div className="p-4 bg-moonbone border border-teal-200 rounded-lg">
                <p className="text-sm text-ink">
                  <strong>Note:</strong> If your browser's "Do Not Track" (DNT) setting is enabled,
                  analytics cookies will be disabled regardless of your choice.
                </p>
              </div>
            </div>

            {/* Custom Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 p-6 bg-gray-50 border-t border-gray-200">
              <button
                onClick={handleBack}
                disabled={isInitializing}
                className="flex-1 bg-white border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Back
              </button>
              <button
                onClick={handleSaveCustom}
                disabled={isInitializing}
                className="flex-1 bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isInitializing ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
    </FocusLock>
  );
}
